import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

export type ConfirmRequest = {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 确认键改成实心红，用于删除这类不可逆操作 */
  danger?: boolean;
};

type Pending = ConfirmRequest & { settle: (ok: boolean) => void };

function ConfirmDialog({ request, onSettle }: {
  request: Pending;
  onSettle: (ok: boolean) => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  // 焦点落在确认键上，回车即可确认——浏览器原生弹窗就是这个手感，别让人多摸一次鼠标
  useEffect(() => { confirmRef.current?.focus(); }, [request]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onSettle(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSettle]);

  return (
    <div className="modal-backdrop" onClick={() => onSettle(false)}>
      <div
        className="modal narrow"
        role="alertdialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{request.title}</h3>
        {request.body && <p className="sub">{request.body}</p>}
        <div className="modal-actions">
          <button type="button" onClick={() => onSettle(false)}>
            {request.cancelLabel ?? 'Cancel'}
          </button>
          <button
            type="button"
            ref={confirmRef}
            className={request.danger ? 'solid-danger' : 'primary'}
            onClick={() => onSettle(true)}
          >
            {request.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * window.confirm 的替代品，保留它 `if (!await confirm(…)) return;` 的调用形状，
 * 所以调用点除了多一个 await 之外不用改写控制流。
 */
export function useConfirm() {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback((request: ConfirmRequest) => new Promise<boolean>((resolve) => {
    setPending({ ...request, settle: resolve });
  }), []);

  const settle = useCallback((ok: boolean) => {
    if (!pending) return;
    pending.settle(ok);
    setPending(null);
  }, [pending]);

  return {
    confirm,
    dialog: pending ? <ConfirmDialog request={pending} onSettle={settle} /> : null,
  };
}
